const path = require('path');
const SassAlias = require('sass-alias');
const publicUrl = process.env.PUBLIC_URL;
const isVercel = process.env.VERCEL === '1';

// Use CSP_REPORT_ONLY env variable to control report-only mode
const cspReportOnly = process.env.CSP_REPORT_ONLY === 'true';
const cspHeaderKey = cspReportOnly
  ? 'Content-Security-Policy-Report-Only'
  : 'Content-Security-Policy';
const disableCors = process.env.DISABLE_CORS === 'true';

/**
 * @type {import('next').NextConfig}
 */
const nextConfig = {
  // Allow easier debugging.  We can remove if clients consider it a security risk
  // but generally it should be fine for most projects.
  productionBrowserSourceMaps: true,

  assetPrefix: isVercel ? undefined : publicUrl,
  // Allow specifying a distinct distDir when concurrently running app in a container
  distDir: process.env.NEXTJS_DIST_DIR || '.next',

  i18n: {
    // These are all the locales you want to support in your application.
    // These should generally match (or at least be a subset of) those in Sitecore.
    locales: ['en', 'es-ES'],
    // This is the locale that will be used when visiting a non-locale
    // prefixed path e.g. `/about`.
    defaultLocale: process.env.DEFAULT_LANGUAGE || process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || 'en',
  },

  // Enable React Strict Mode
  reactStrictMode: true,

  // Ensure proper client-side navigation
  experimental: {
    scrollRestoration: true,
  },

  // Disable the X-Powered-By header. Follows security best practices.
  poweredByHeader: false,

  // use this configuration to ensure that only images from the whitelisted domains
  // can be served from the Next.js Image Optimization API
  // see https://nextjs.org/docs/app/api-reference/components/image#remotepatterns
  images: {
    remotePatterns: [
      {
        protocol: 'https',
        hostname: 'edge*.**',
        port: '',
      },
      {
        protocol: 'https',
        hostname: 'xmc-*.**',
        port: '',
      },
    ],
    localPatterns: [
      {
        pathname: '/-/media/**',
      },
      {
        pathname: '/assets/Images/**',
      }
    ],
  },

  async headers() {
    // Fetch CSP directives dynamically from Sitecore
    // const cspDirectives = await getCSPDirectives();

    // const reportTo = JSON.stringify({
    //   group: 'csp-endpoint',
    //   max_age: 86400, // seconds (125 days)
    //   endpoints: [{ url: `${process.env.PUBLIC_URL}/api/csp-report/xt2c9f8er8` }],
    // });

    return [
      {
        source: '/_next/:path*',
        headers: [
          {
            key: 'Access-Control-Allow-Origin',
            value: process.env.NEXT_PUBLIC_SITECORE_API_HOST?.replace(/\/$/, ''),
          },
          {
            key: 'Access-Control-Allow-Methods',
            value: 'GET, POST, OPTIONS',
          },
          {
            key: 'Access-Control-Allow-Headers',
            value: 'X-Requested-With, Content-Type, Authorization',
          },
        ],
      },
      {
        source: '/api/editing/render',
        headers: [
          {
            key: 'Content-Type', // Specify content type to avoid having to sniff from our editing endpoint
            value: 'text/html; charset=utf-8',
          }
        ],
      },
      {
        source: '/:path*',
        headers: [
          {
            key: 'X-DNS-Prefetch-Control',
            value: 'on',
          },
          {
            key: 'X-XSS-Protection',
            value: '1; mode=block',
          },
          {
            key: 'Access-Control-Allow-Origin',
            value: process.env.NEXT_PUBLIC_SITECORE_API_HOST.replace(/\/$/, ''),
          },
          //security scan issue fix - ticket 1090
          {
            key: 'Strict-Transport-Security',
            value: 'max-age=63072000; includeSubDomains; preload',
          },
          // {
          //   key: cspHeaderKey,
          //   value: cspDirectives,
          // },
          // {
          //   key: 'Report-To',
          //   value: reportTo,
          // },
          {
            key: 'Cross-Origin-Opener-Policy',
            value: 'same-origin',
          },
          {
            key: 'Cross-Origin-Resource-Policy',
            value: 'cross-origin',
          },
          {
            key: 'Cross-Origin-Embedder-Policy',
            value: 'unsafe-none',
          },
          { key: 'X-Content-Type-Options', value: 'nosniff' },
          { key: 'Referrer-Policy', value: 'strict-origin-when-cross-origin' },
          { key: 'Permissions-Policy', value: 'geolocation=(self), camera=(), microphone=()' },
        ],
      },
    ];
  },

  async rewrites() {
    return [
      // Sitecore rewrites
      // healthz check
      {
        source: '/healthz',
        destination: '/api/healthz',
      },
      // media items
      {
        source: '/-/:path*',
        destination: `${process.env.NEXT_PUBLIC_SITECORE_API_HOST}/-/:path*`,
      },
      // robots route
      {
        source: '/robots.txt',
        destination: '/api/robots',
      },
      // sitemap route
      {
        source: '/sitemap:id([\\w-]{0,}).xml',
        destination: '/api/sitemap',
      },
      // feaas api route
      {
        source: '/feaas-render',
        destination: '/api/editing/feaas/render',
      },
      // Add cms-hpt file required for regulatory purposes with a rewrite from the media library so that it can be modified
      {
        source: '/cms-hpt.txt',
        destination:
          'https://edge.sitecorecloud.io/childrensho3e59-cw60b4-prod2126-77fc/media/CHWLIBRARY/Files/Patients-and-families/Billing-payment/cms-hpt.txt',
      },
    ];
  },

  // redirect rules for teaching sheet and providers.
  async redirects() {
    return [
      {
        source: '/home',
        destination: '/',
        permanent: true,
      },
      {
        source: '/home/:path*',
        destination: '/:path*',
        permanent: true,
      },
      {
        source: '/publications/teaching-sheet/:lang/:id(\\d+)-:cont(.*)',
        destination: '/teaching-sheet/:lang/:cont',
        permanent: true,
      },
      {
        source: '/es-es/publications/teaching-sheet/:lang/:id(\\d+)-:cont(.*)',
        destination: '/es-es/teaching-sheet/:lang/:cont',
        permanent: true,
      },
      {
        source: '/physician-directory',
        destination: '/providers',
        permanent: true,
      },
      {
        source: '/physician-directory/:cat([a-zA-Z0-9-]+)/:name([^/]+)',
        destination: '/providers/:cat/:name',
        permanent: true,
      },
      // Temp until JSS 22.9 update
      {
        source: '/4rhett',
        destination:
          'https://give.childrenswi.org/page/88753/donate/1?&utm_campaign=2025-yearend-appeal&utm_medium=mailvanity&utm_source=ag-don-mail-1&utm_term=mailing',
        permanent: true,
      },
      {
        source: '/4kids',
        destination: 'https://give.childrenswi.org/page/88784/donate/1',
        permanent: true,
      },
      // Domain-based rules - ticket 1098
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'cssw.org' }],
        destination: 'https://childrenswi.org/who-we-are/community-programs',
        permanent: true,
      },
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'kidhero.chw.org' }],
        destination: 'https://childrenswi.org/who-we-are/community-programs/foster-care',
        permanent: true,
      },
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'skywalkpharmacy.com' }],
        destination: 'https://childrenswi.org/locations/skywalk-milwaukee',
        permanent: true,
      },
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'shinethrough.childrenswi.org' }],
        destination: 'https://childrenswi.org/find-care/mental-behavioral-health',
        permanent: true,
      },
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'verificationletters.chw.org' }],
        destination:
          'https://childrenswi.org/medical-professionals/patient-referral-toolkit/staff-verification',
        permanent: true,
      },
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'blog.chw.org' }],
        destination: 'https://childrenswi.org/at-every-turn',
        permanent: true,
      },

      // New rules for givings site - ticket 1098
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'alsrun.com' }],
        destination: 'https://giving.childrenswi.org/Als-Run-Walk',
        permanent: true,
      },
      {
        source: '/:path*',
        has: [{ type: 'host', value: 'miraclemarathon.chw.org' }],
        destination: 'https://giving.childrenswi.org/events',
        permanent: true,
      },
    ];
  },

  webpack: (config, options) => {
    if (!options.isServer) {
      // Add a loader to strip out getComponentServerProps from components in the client bundle
      config.module.rules.unshift({
        test: /src\\components\\.*\.tsx$/,
        use: ['@sitecore-content-sdk\\nextjs\\component-props-loader'],
      });
    } else {
      // Force use of CommonJS on the server for FEAAS SDK since Content SDK also uses CommonJS entrypoint to FEAAS SDK.
      // This prevents issues arising due to FEAAS SDK's dual CommonJS/ES module support on the server (via conditional exports).
      // See https://nodejs.org/api/packages.html#dual-package-hazard.
      config.externals = [
        {
          '@sitecore-feaas/clientside/react': 'commonjs @sitecore-feaas/clientside/react',
          '@sitecore/byoc': 'commonjs @sitecore/byoc',
          '@sitecore/byoc/react': 'commonjs @sitecore/byoc/react',
        },
        ...config.externals,
      ];
    }

    return config;
  },

  // Add sass settings for SXA themes and styles
  sassOptions: {
    importer: new SassAlias({
      '@globals': path.join(process.cwd(), './src/assets', 'globals'),
      '@fontawesome': path.join(process.cwd(), './node_modules', 'font-awesome'),
    }).getImporter(),
    // temporary measure until new versions of bootstrap and font-awesome released
    quietDeps: true,
    silenceDeprecations: ['import', 'legacy-js-api'],
  },
};

module.exports = nextConfig;
